import type { GelanggangDetail, NoticeItem } from '../types/index';

export interface ScheduledActivity {
  id: string;
  title: string;
  type: 'latihan' | 'acara' | 'notis';
  startTime: Date;
  endTime: Date;
  isLive: boolean; // now >= startTime && now <= endTime
  isToday: boolean;
  dayRelative: string; // e.g. "Hari Ini", "Esok", "Jumaat"
  dateLabel: string; // e.g. "Hari Ini, 8 Sep" or "Jumaat, 11 Sep"
  timeRange: string; // e.g. "8.00 AM - 10.00 AM"
  location?: string;
  leaderOrAuthor?: string;
}

const MALAY_DAYS = [
  { name: 'ahad', dayIndex: 0, label: 'Ahad' },
  { name: 'isnin', dayIndex: 1, label: 'Isnin' },
  { name: 'selasa', dayIndex: 2, label: 'Selasa' },
  { name: 'rabu', dayIndex: 3, label: 'Rabu' },
  { name: 'khamis', dayIndex: 4, label: 'Khamis' },
  { name: 'jumaat', dayIndex: 5, label: 'Jumaat' },
  { name: 'sabtu', dayIndex: 6, label: 'Sabtu' },
];

const SHORT_MONTHS = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];

/**
 * Parses start and end time from strings like:
 * - "(8.00 AM)" -> 8:00 to 10:00
 * - "Jumaat & Sabtu (8.30 PM)" -> 20:30 to 22:30
 * - "Rabu & Sabtu (9.00 PM)" -> 21:00 to 23:00
 * - "8.00 AM - 10.30 AM" -> 8:00 to 10:30
 * - "9.00 Malam" -> 21:00 to 23:00
 */
export function parseStartAndEndTime(rawTime?: string): {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  formattedRange: string;
} {
  let startHour = 8;
  let startMinute = 0;
  let endHour = 10;
  let endMinute = 0;

  if (!rawTime) {
    return { startHour, startMinute, endHour, endMinute, formattedRange: '8.00 AM - 10.00 AM' };
  }

  // Extract content inside parenthesis if exists
  const parenMatch = rawTime.match(/\(([^)]+)\)/);
  const target = (parenMatch ? parenMatch[1] : rawTime).trim();

  // Pattern 1: Range like "8.00 AM - 10.00 AM" or "8:30 PM - 10:30 PM"
  const rangeRegex = /(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?\s*[-–—to]+\s*(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?/i;
  const rangeMatch = target.match(rangeRegex);

  if (rangeMatch) {
    let h1 = parseInt(rangeMatch[1], 10);
    const m1 = rangeMatch[2] ? parseInt(rangeMatch[2], 10) : 0;
    const p1 = rangeMatch[3] ? rangeMatch[3].toUpperCase() : '';

    let h2 = parseInt(rangeMatch[4], 10);
    const m2 = rangeMatch[5] ? parseInt(rangeMatch[5], 10) : 0;
    const p2 = rangeMatch[6] ? rangeMatch[6].toUpperCase() : p1;

    if ((p2 === 'PM' || /malam|petang/i.test(p2)) && h2 < 12) h2 += 12;
    if (p2 === 'AM' && h2 === 12) h2 = 0;

    const p1Final = p1 || p2;
    if ((p1Final === 'PM' || /malam|petang/i.test(p1Final)) && h1 < 12) h1 += 12;
    if (p1Final === 'AM' && h1 === 12) h1 = 0;

    startHour = h1;
    startMinute = m1;
    endHour = h2;
    endMinute = m2;

    const fStart = formatTime12(startHour, startMinute);
    const fEnd = formatTime12(endHour, endMinute);
    return { startHour, startMinute, endHour, endMinute, formattedRange: `${fStart} - ${fEnd}` };
  }

  // Pattern 2: Single time e.g. "8.00 AM" or "8.30 PM" or "9.00 Malam"
  const singleRegex = /(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?/i;
  const singleMatch = target.match(singleRegex);

  if (singleMatch) {
    let h = parseInt(singleMatch[1], 10);
    const m = singleMatch[2] ? parseInt(singleMatch[2], 10) : 0;
    const period = singleMatch[3] ? singleMatch[3].toUpperCase() : '';

    if ((period === 'PM' || /malam|petang/i.test(period)) && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;

    startHour = h;
    startMinute = m;

    // Default duration is 2 hours (e.g. 8am to 10am, or 8:30pm to 10:30pm)
    endHour = (h + 2) % 24;
    endMinute = m;

    const fStart = formatTime12(startHour, startMinute);
    const fEnd = formatTime12(endHour, endMinute);
    return { startHour, startMinute, endHour, endMinute, formattedRange: `${fStart} - ${fEnd}` };
  }

  return { startHour, startMinute, endHour, endMinute, formattedRange: '8.00 AM - 10.00 AM' };
}

function formatTime12(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const mStr = minute < 10 ? `0${minute}` : `${minute}`;
  return `${h12}.${mStr} ${period}`;
}

export function parseTrainingDays(sessionTime?: string): number[] {
  if (!sessionTime) return [5, 6];
  const lower = sessionTime.toLowerCase();
  const days: number[] = [];

  MALAY_DAYS.forEach(({ name, dayIndex }) => {
    if (lower.includes(name)) {
      days.push(dayIndex);
    }
  });

  return days.length > 0 ? days : [5, 6];
}

/**
 * Returns the next latest activity scheduled for a given gelanggang.
 *
 * CRITICAL RULE:
 * An activity remains the active display until the current time has passed
 * its END TIME (`now < endTime`).
 *
 * Example:
 * If a session is today 8.00 AM - 10.00 AM and now is 9.00 AM:
 * `now < 10.00 AM` -> it still shows today's session!
 * Only once `now >= 10.00 AM` does it roll over to the next scheduled session.
 */
export function getNextScheduledActivity(
  gelanggang: GelanggangDetail,
  notices: NoticeItem[] = [],
  currentTime: Date = new Date()
): ScheduledActivity | null {
  const now = currentTime;
  const candidates: ScheduledActivity[] = [];

  const trainingDays = parseTrainingDays(gelanggang.sessionTime);
  const { startHour, startMinute, endHour, endMinute, formattedRange } = parseStartAndEndTime(
    gelanggang.sessionTime
  );

  // 1. Generate regular weekly training sessions (including yesterday offset -1 in case of overnight session ending today)
  for (let offset = -1; offset <= 14; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    const dayOfWeek = d.getDay();

    if (trainingDays.includes(dayOfWeek)) {
      const startTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), startHour, startMinute, 0, 0);
      const endTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), endHour, endMinute, 0, 0);

      if (endHour < startHour) {
        // Spans midnight into the next day
        endTime.setDate(endTime.getDate() + 1);
      }

      // If current time has NOT passed the end time, this session is active/upcoming!
      if (now <= endTime) {
        const isLive = now >= startTime && now <= endTime;
        const isToday = offset === 0 || (offset === -1 && isLive);
        const dayMalay = MALAY_DAYS.find((m) => m.dayIndex === dayOfWeek)?.label || '';
        const dayRelative = isToday ? 'Hari Ini' : offset === 1 ? 'Esok' : dayMalay;
        const dateLabel = isToday
          ? `Hari Ini, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
          : offset === 1
          ? `Esok (${dayMalay}), ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
          : `${dayMalay}, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;

        candidates.push({
          id: `session-${gelanggang.id}-${d.toISOString().slice(0, 10)}`,
          title: 'Sesi Latihan Gelanggang',
          type: 'latihan',
          startTime,
          endTime,
          isLive,
          isToday,
          dayRelative,
          dateLabel,
          timeRange: formattedRange,
          location: gelanggang.location,
          leaderOrAuthor: gelanggang.leaderName,
        });
      }
    }
  }

  // 2. Generate activities from Notices / Acara / Ujian Bengkung targeting this gelanggang
  notices.forEach((n) => {
    if (!n.eventDate) return;

    // Check target gelanggang match
    const matchesGelanggang =
      !n.targetGelanggang ||
      n.targetGelanggang === 'Semua Gelanggang' ||
      n.targetGelanggang === 'PSSASMM' ||
      n.targetGelanggang === gelanggang.name;

    if (!matchesGelanggang) return;

    // Parse date (YYYY-MM-DD)
    const dateParts = n.eventDate.split('T')[0].split('-');
    if (dateParts.length < 3) return;

    const y = parseInt(dateParts[0], 10);
    const m = parseInt(dateParts[1], 10) - 1;
    const day = parseInt(dateParts[2], 10);

    const timeParsed = parseStartAndEndTime(n.eventTime);
    const startTime = new Date(y, m, day, timeParsed.startHour, timeParsed.startMinute, 0, 0);
    const endTime = new Date(y, m, day, timeParsed.endHour, timeParsed.endMinute, 0, 0);

    if (timeParsed.endHour < timeParsed.startHour) {
      endTime.setDate(endTime.getDate() + 1);
    }

    // Only consider if not ended yet
    if (now <= endTime) {
      const isLive = now >= startTime && now <= endTime;
      const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const eventDateOnly = new Date(y, m, day).getTime();
      const diffDays = Math.round((eventDateOnly - todayDateOnly) / 86400000);
      const isToday = diffDays === 0;

      const dayIndex = startTime.getDay();
      const dayMalay = MALAY_DAYS.find((item) => item.dayIndex === dayIndex)?.label || '';
      const dayRelative = isToday ? 'Hari Ini' : diffDays === 1 ? 'Esok' : dayMalay;
      const dateLabel = isToday
        ? `Hari Ini, ${day} ${SHORT_MONTHS[m]}`
        : diffDays === 1
        ? `Esok (${dayMalay}), ${day} ${SHORT_MONTHS[m]}`
        : `${dayMalay}, ${day} ${SHORT_MONTHS[m]}`;

      candidates.push({
        id: `notice-${n.id}`,
        title: n.title,
        type: n.type === 'acara' ? 'acara' : 'notis',
        startTime,
        endTime,
        isLive,
        isToday,
        dayRelative,
        dateLabel,
        timeRange: n.eventTime || timeParsed.formattedRange,
        location: n.eventLocation || gelanggang.location,
        leaderOrAuthor: n.authorName,
      });
    }
  });

  if (candidates.length === 0) return null;

  // Sort by startTime ascending (earliest first)
  candidates.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  return candidates[0];
}
